/** biome-ignore-all lint/suspicious/noExplicitAny: token shapes come from markdown-it and Markdoc */

import type { PreTransformerFunc } from "@ext/markdown/core/Parser/Transformer/preTransformTokens";
import { COMMENT_BLOCK_NODE_TYPES } from "@ext/markdown/elements/comment/edit/model/consts";

const COMMENT_TAG = "comment";

const isCommentTag = (token: any, type: "tag_open" | "tag_close") =>
	token?.type === type && token?.meta?.tag === COMMENT_TAG;

const commentIdOf = (token: any): string =>
	token?.meta?.attributes?.find((attr: any) => attr.name === "id")?.value ?? null;

const commentTagToken = (type: "tag_open" | "tag_close", id: string) => ({
	type,
	tag: "",
	attrs: null,
	content: "",
	nesting: type === "tag_open" ? 1 : -1,
	meta:
		type === "tag_open"
			? { tag: COMMENT_TAG, attributes: [{ type: "attribute", name: "id", value: id }] }
			: { tag: COMMENT_TAG },
});

const setComment = (token: any, id: string) => {
	if (Array.isArray(token.attrs)) {
		token.attrs = token.attrs.filter(([name]: [string]) => name !== COMMENT_TAG);
		token.attrs.push([COMMENT_TAG, { id }]);
	} else if (token.attrs && typeof token.attrs === "object") {
		token.attrs[COMMENT_TAG] = { id };
	} else {
		token.attrs = [[COMMENT_TAG, { id }]];
	}

	if (token.meta?.tag) {
		const attributes = (token.meta.attributes ?? []).filter((attr: any) => attr.name !== COMMENT_TAG);
		attributes.push({ type: "attribute", name: COMMENT_TAG, value: { id } });
		token.meta.attributes = attributes;
	}
};

/**
 * `<comment id="1"><openapi src="./api.yaml"/></comment>` on one line is read as a paragraph with a block
 * inside, which is a parse error: the comment moves onto the element, the element onto block level.
 */
const liftWrappedBlock = (tokens: any[]) => {
	for (let i = 0; i < tokens.length; i++) {
		if (tokens[i].type !== "paragraph_open") continue;
		const inline = tokens[i + 1];
		if (inline?.type !== "inline" || tokens[i + 2]?.type !== "paragraph_close") continue;

		const children = inline.children ?? [];
		const open = children[0];
		const close = children[children.length - 1];
		if (!isCommentTag(open, "tag_open") || !isCommentTag(close, "tag_close")) continue;

		// `![](a.png){width=100px}` leaves an empty text token behind the image; it is not content
		const inner = children.slice(1, -1).filter((token: any) => token.type !== "text" || token.content);
		if (inner.length !== 1) continue;
		const wrapped = inner[0];

		const id = commentIdOf(open);
		if (!id) continue;

		if (wrapped?.type === "tag" && COMMENT_BLOCK_NODE_TYPES.includes(wrapped?.meta?.tag)) {
			setComment(wrapped, id);
			tokens.splice(i, 3, wrapped);
			continue;
		}

		// an image is turned into a node by imageTransform later; it only needs the comment on the token
		if (wrapped?.tag === "img") {
			setComment(wrapped, id);
			inline.children = [wrapped];
		}
	}
};

const afterMatchingClose = (tokens: any[], start: number) => {
	let depth = 0;

	for (let i = start; i < tokens.length; i++) {
		if (tokens[i].type.endsWith("_open")) depth++;
		if (tokens[i].type.endsWith("_close") && --depth === 0) return i + 1;
	}

	return tokens.length;
};

const matchingCommentClose = (tokens: any[], open: number) => {
	let depth = 0;

	for (let i = open; i < tokens.length; i++) {
		if (isCommentTag(tokens[i], "tag_open")) depth++;
		if (isCommentTag(tokens[i], "tag_close") && --depth === 0) return i;
	}

	return -1;
};

/**
 * A comment on its own lines wraps every top-level block up to the closing tag: a block keeps it in its
 * attributes, a paragraph as a mark on its text. Nested content is left alone, or the comment would be recorded twice.
 */
const markWrappedBlocks = (tokens: any[]) => {
	for (let i = 0; i < tokens.length; i++) {
		if (!isCommentTag(tokens[i], "tag_open")) continue;

		const id = commentIdOf(tokens[i]);
		const end = matchingCommentClose(tokens, i);
		if (!id || end === -1) continue;

		const body = tokens.slice(i + 1, end);
		let cursor = 0;

		while (cursor < body.length) {
			const token = body[cursor];

			if (!token.type.endsWith("_open")) {
				setComment(token, id);
				cursor++;
				continue;
			}

			if (token.type === "paragraph_open") {
				const inline = body[cursor + 1];
				if (inline?.type === "inline") {
					inline.children = [
						commentTagToken("tag_open", id),
						...(inline.children ?? []),
						commentTagToken("tag_close", id),
					];
				}
			} else {
				setComment(token, id);
			}

			cursor = afterMatchingClose(body, cursor);
		}

		// the body is walked again: a block inside it may carry a comment of its own
		tokens.splice(i, end - i + 1, ...body);
		i--;
	}
};

/** `<comment id="1"/>` wraps nothing; handed on, the block parser would look for its pair and fail the article. */
const dropSelfClosingComments = (tokens: any[]) => {
	const isSelfClosing = (token: any) => token?.type === "tag" && token?.meta?.tag === COMMENT_TAG;

	for (let i = tokens.length - 1; i >= 0; i--) {
		if (isSelfClosing(tokens[i])) tokens.splice(i, 1);
		if (!tokens[i]?.children) continue;
		tokens[i].children = tokens[i].children.filter((child: any) => !isSelfClosing(child));
	}
};

const blockCommentTransformer: PreTransformerFunc = ({ tokens }) => {
	dropSelfClosingComments(tokens);
	markWrappedBlocks(tokens);
	liftWrappedBlock(tokens);
	return tokens;
};

export default blockCommentTransformer;
